<template>
  <a-modal
    :title="`转换与颜色 —— ${record?.title || record?.data_index || ''}`"
    width="760px"
    :open="open"
    cancel-text="取消"
    ok-text="确定"
    centered
    destroy-on-close
    @cancel="handleCancel"
    @ok="handleOk"
  >
    <div class="map-tip">
      原值 → 显示文本 与 原值 → 颜色 在此一体化配置：一行 =
      一条「原值」的转换与颜色，落库时拆回两个键。 最右「预览」按运行时标签组件的真实样式渲染 ——
      内置色是浅底彩字、 自定义色是实心彩底白字，两者观感不同属正常。
    </div>
    <a-table
      :data-source="rows"
      :columns="columns"
      row-key="__k"
      size="small"
      :pagination="false"
    >
      <template #bodyCell="{ column, record: row }">
        <template v-if="column.key === 'value'">
          <a-input
            v-model:value="row.value"
            size="small"
            placeholder="原值"
            allow-clear
          />
        </template>
        <template v-else-if="column.key === 'label'">
          <a-input
            v-model:value="row.label"
            size="small"
            placeholder="显示文本"
            allow-clear
          />
        </template>
        <template v-else-if="column.key === 'color'">
          <div class="color-cell">
            <!-- ★ v29.23（用户定案）：内置色板直接存颜色名字符串（pink/red/…，不转十六进制）；
                 自定义走颜色选择器（选择器产出 #十六进制），不再让用户手输。
                 ★ v29.23.2：a-color-picker 在 antdv 4.2.6 中不存在（es 目录无 color-picker，
                 模板里写它是未知组件被静默渲染为空——「自定义颜色没出现」的根因），
                 改用原生 input[type=color]（产出 #rrggbb，同尺寸色块形态）。 -->
            <div class="swatches">
              <span
                v-for="c in PRESET_COLORS"
                :key="c"
                class="swatch"
                :class="{ active: row.color === c }"
                :style="{ background: PRESET_HEX[c] }"
                :title="c"
                @click="row.color = c"
              />
              <span class="custom-color-label">自定义：</span>
              <input
                type="color"
                class="custom-color-input"
                :class="{ active: isCustomColor(row) }"
                :value="row.color && row.color.startsWith('#') ? row.color : '#1677ff'"
                :title="isCustomColor(row) ? row.color : '自定义颜色'"
                @input="onCustomColor(row, $event)"
              />
            </div>
          </div>
        </template>
        <template v-else-if="column.key === 'preview'">
          <!-- ★ v29.39（用户定案）：a-tag 实时预览 —— 把「原值 + 显示文本 + 颜色」按运行时
               cell_tag.vue 的语义渲染出来，配色的观感当场可见（色名浅底彩字 / 十六进制实心彩底白字）。
               ⚠️ 文案与颜色必须与 liyu-pc-lib/src/components/cellComponent/cell_tag.vue 逐条对齐，
                  见脚本里 previewText / previewColor 的注释；这里显示什么，真机就长什么样。 -->
          <div class="preview-cell">
            <a-tag
              v-if="previewText(row)"
              :color="previewColor(row)"
            >
              {{ previewText(row) }}
            </a-tag>
            <span
              v-else
              class="preview-empty"
            >
              —
            </span>
          </div>
        </template>
        <template v-else-if="column.key === 'action'">
          <a-button
            type="link"
            danger
            size="small"
            style="padding: 0"
            @click="removeRow(row.__k)"
          >
            删除
          </a-button>
        </template>
      </template>
    </a-table>
    <a-button
      size="small"
      type="dashed"
      block
      style="margin-top: 8px"
      @click="addRow"
    >
      + 添加一条转换
    </a-button>
  </a-modal>
</template>

<script setup lang="ts">
import { message } from 'ant-design-vue';
import { computed, ref, watch } from 'vue';

/* ★ v29.16（用户定案）：content_map（原值→显示文本）与 color_map（原值→颜色）不再割裂为
   两个配置入口，而是一张「转换与颜色」联合表 —— 一行 = {原值, 显示文本, 颜色}，
   确定时按原值拆回 content_map / color_map 两个键写回行记录（运行时契约不变）。 */

interface Props {
  open: boolean;
  record: any; // 列配置行（colRows 中的一项，直接读写其 content_map / color_map）
}
const props = defineProps<Props>();
const open = defineModel<boolean>('open', { default: false });

const columns: any[] = [
  { title: '原值', key: 'value', width: '18%' },
  { title: '显示文本', key: 'label', width: '18%' },
  { title: '颜色', key: 'color', width: '36%' },
  { title: '预览', key: 'preview', width: '20%' },
  { title: '', key: 'action', width: '8%' },
];

/* ★ v29.23（用户定案）：内置色板直接存 antd 预设色**名字符串**（red/orange/green/cyan/
   blue/purple，运行时 tag/色块组件按名字渲染），不转十六进制；PRESET_HEX 仅用于色板展示。
   自定义 = 颜色选择器（a-color-picker），产出 #十六进制字符串。
   ⚠️ `pink` 已从色板移除：历史数据里存的 `pink` 运行时仍能正常渲染（antd 侧自带该预设色），
   但配置弹窗里**既不会点亮色块、也不会点亮取色器**（`pink` 不以 `#` 开头，又不在 PRESET_COLORS 里）
   —— 这个「0 个亮」是已知退化，已由技能里的 preview-vs-runtime-check.mjs 断言钉住。 */
const PRESET_COLORS = ['red', 'orange', 'green', 'cyan', 'blue', 'purple'];
const PRESET_HEX: Record<string, string> = {
  red: '#f5222d',
  orange: '#fa8c16',
  green: '#52c41a',
  cyan: '#13c2c2',
  blue: '#1677ff',
  purple: '#722ed1',
};

/* 自定义取色：原生 input[type=color] 事件值恒为 #rrggbb 字符串（v29.23.2，替代不存在的 a-color-picker） */
const onCustomColor = (row: any, e: Event) => {
  row.color = (e.target as HTMLInputElement).value;
};

/* ★ v29.39（用户定案）：自定义颜色也要有「已选中」高亮，与内置色块的 .active 同款。
   判定取「值是 # 开头的十六进制」= 取色器的产出形态 ——
   ① 与取色器 :value 的取值判定同源，不会出现「高亮了但取色器显示的并不是这个色」；
   ② 也与运行时一致：cell_tag.vue 把该值直接交给 a-tag 的 color，
      antd 内部按「是否预设色名」分两路渲染（色名 → 浅底彩字 / 十六进制 → 实心彩底白字），
      所以「# 开头」正是「走了自定义分支」的准确刻画。 */
const isCustomColor = (row: any) =>
  String(row.color || '')
    .trim()
    .startsWith('#');

let seq = 0;
const rows = ref<any[]>([]);
const buildRow = (value = '', label = '', color = '') => ({ __k: ++seq, value, label, color });

/* 每次打开时从行记录重建本地编辑态（content_map/color_map → 联合行） */
watch(
  () => props.open,
  val => {
    if (!val) return;
    seq = 0;
    const cm = props.record?.content_map || {};
    const colors = props.record?.color_map || {};
    const keys = Array.from(new Set([...Object.keys(cm || {}), ...Object.keys(colors || {})]));
    rows.value = keys.length
      ? keys.map(k => buildRow(String(k), cm?.[k] ?? '', colors?.[k] ?? ''))
      : [buildRow()];
  },
);

const addRow = () => rows.value.push(buildRow());
const removeRow = (k: number) => {
  rows.value = rows.value.filter(r => r.__k !== k);
};

/* ★ v29.39：a-tag 预览 —— 与运行时 cell_tag.vue 一一对应，保证「所见即真机所得」。
   运行时（liyu-pc-lib/src/components/cellComponent/cell_tag.vue）的算法：
     tagText  = contentMap[text] || contentMap['other'] || text        （text = 单元格原始值）
     tagColor = colorMap ? (colorMap[text] || colorMap['other']) : undefined
   其中 `other` 是约定的兜底键：任何未命中的原始值都落到它。
   ⚠️ 外层 `v-if="cmpObj.column.contentMap"` 看似是「没配转换就不渲染」，实际不成立 ——
      后端 columns_config() 产出的是 `c.content_map or {}`（空 map → 空对象，truthy），
      所以「只配颜色」的列同样会渲染标签，文字退回原始值。预览照此实现，不另加限制。 */

/* 兜底行 = 原值填 `other` 的那一行（可能不存在）。
   取**最后一条**与 handleOk 的落库口径一致（重复原值时保留最后一次），否则预览会和真机不符。 */
const otherRow = computed(() => {
  const hits = rows.value.filter(r => (r.value || '').trim() === 'other');
  return hits.length ? hits[hits.length - 1] : undefined;
});

/* 预览文案：本行显示文本 → `other` 兜底显示文本 → 原始值 */
const previewText = (row: any) => {
  const v = (row.value || '').trim();
  if (!v) return ''; // 无原值 → 该行谈不上渲染
  const label = (row.label || '').trim() || (otherRow.value?.label || '').trim();
  return label || v;
};

/* 预览颜色：本行颜色 → `other` 兜底颜色；都没有 → undefined（a-tag 默认样式，与运行时一致） */
const previewColor = (row: any) => {
  if (!(row.value || '').trim()) return undefined;
  const color = (row.color || '').trim() || (otherRow.value?.color || '').trim();
  return color || undefined;
};

const handleOk = () => {
  const content_map: Record<string, string> = {};
  const color_map: Record<string, string> = {};
  for (const r of rows.value) {
    const v = (r.value || '').trim();
    if (!v) continue; // 空原值行直接忽略
    if (v in content_map) {
      message.warning(`原值「${v}」重复，已保留最后一次配置`);
    }
    const label = (r.label || '').trim();
    const color = (r.color || '').trim();
    if (label) content_map[v] = label;
    if (color) color_map[v] = color;
  }
  props.record.content_map = Object.keys(content_map).length ? content_map : null;
  props.record.color_map = Object.keys(color_map).length ? color_map : null;
  /* ★ v29.23（用户定案）：确定时若有配置项 → 单元格组件自动置为 tag（仅自动改，
     用户随后可手动改成其他组件，不强制） */
  if (props.record.content_map || props.record.color_map) {
    props.record.cell_component = 'tag';
  }
  open.value = false;
};
const handleCancel = () => {
  open.value = false;
};
</script>

<style scoped lang="less">
.map-tip {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 8px;
  line-height: 1.5;
}
.color-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}
/* ★ v29.39：a-tag 预览列。窄列里必须收掉 a-tag 自带的 margin-inline-end（antd 默认 8px），
   否则标签会被挤出单元格；显示文本过长按省略号截断，不换行、不撑高行。 */
.preview-cell {
  display: flex;
  align-items: center;
  overflow: hidden;
  :deep(.ant-tag) {
    margin-inline-end: 0;
    max-width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}
.preview-empty {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.25);
}
.swatches {
  display: flex;
  gap: 4px;
  flex: none;
}
.swatch {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.15);
}
/* 选中态高亮：内置色块与自定义取色器**同口径**（v29.39 用户定案：配了自定义色也要高亮，
   否则用户看不出当前行到底选的是色板还是取色器）。放在两者之后，保证覆盖各自的基础样式。 */
.swatch.active,
.custom-color-input.active {
  outline: 2px solid #1677ff;
  outline-offset: 1px;
}
.custom-color-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  line-height: 16px;
}
/* 原生取色输入：整成与色块同尺寸的小方块（点击弹系统取色器） */
.custom-color-input {
  width: 16px;
  height: 16px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }
  &::-webkit-color-swatch {
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 3px;
  }
}
</style>
